import { z } from 'zod';

/**
 * Preprocessor that coerces a value to a string (unless it's null or undefined),
 * and then converts empty strings or original null/undefined to undefined.
 */
const coerceAndEmptyToUndefinedPreprocessor = (val: string | number | null | undefined) => {
  if (val === null || val === undefined) {
    return undefined;
  }
  const strVal = String(val);
  if (strVal.trim() === '') {
    return undefined;
  }
  return val;
};

/**
 * Preprocessor that filters out empty strings from string arrays.
 */
const filterValidStringArrayPreprocessor = (val: unknown) => {
  if (!Array.isArray(val)) return val;
  return val.filter((item) => typeof item === 'string' && item.trim() !== '');
};

/**
 * A Zod schema for optional strings and numbers
 * and transforms empty strings to undefined before validation.
 */
export const zCoercedOptional = <T>(schema: z.ZodType<T>) =>
  z.preprocess(coerceAndEmptyToUndefinedPreprocessor, schema) as z.ZodType<T>;

/**
 * A Zod schema that filters out empty strings from string arrays before validation.
 */
export const zFilteredStringArray = <T>(schema: z.ZodType<T>) =>
  z.preprocess(filterValidStringArrayPreprocessor, schema) as z.ZodType<T>;

/**
 * A Zod schema for optional arrays that transforms empty strings to undefined,
 *  and then filters out empty values.
 */
export const zCoercedArrayWithoutEmptyValues = <T>(schema: z.ZodType<T[] | undefined>) =>
  z.preprocess((val) => {
    if (!val || !Array.isArray(val)) return undefined;
    if (val.length === 0) {
      return undefined;
    }
    return val.filter((v) => v !== undefined && v !== null && v !== '');
  }, schema) as z.ZodType<T[] | undefined>;

/**
 * Creates a Zod schema for a required UUID string.
 * It provides a shared error message for both invalid type and invalid UUID format.
 * @param message The error message to display.
 */
export const zRequiredUuid = (message: string) =>
  z
    .string({
      invalid_type_error: message,
      required_error: message,
    })
    .uuid({ message });

/**
 * Preprocessor that filters out objects where all properties are empty/falsy values.
 */
type FilterEmptyObjectsFromArrayOptions = {
  /**
   * Whether to filter out objects with all empty/falsy values from object arrays before validation.
   */
  ignoreKeys?: string[];
};

/**
 * Creates a Zod array schema that filters out empty objects before validation.
 * This ensures validation error indices match the filtered array.
 */
export const zArrayWithoutEmptyObjectSchema = <T extends z.ZodTypeAny>(
  itemSchema: T,
  options?: FilterEmptyObjectsFromArrayOptions,
) => {
  /**
   * Helper that determines whether an object has any meaningful (non-empty) value
   * after optionally excluding some keys.
   */
  const isNonEmptyObject = (obj: unknown): obj is Record<string, unknown> => {
    if (typeof obj !== 'object' || obj === null) return false;

    const allKeys = Object.keys(obj);
    const keysToCheck = options?.ignoreKeys ? allKeys.filter((key) => !options.ignoreKeys!.includes(key)) : allKeys;

    return keysToCheck.some((key) => {
      const value = (obj as Record<string, unknown>)[key];
      return value !== undefined && value !== null && value !== '';
    });
  };

  return (
    z
      // Accept any array shape initially so we can run custom validation logic.
      .array(z.any())
      // Custom validation that preserves the original index when adding issues.
      .superRefine((arr, ctx) => {
        arr.forEach((item, index) => {
          // Skip completely empty objects.
          if (!isNonEmptyObject(item)) return;

          // Validate non-empty objects against the provided item schema.
          const result = itemSchema.safeParse(item);

          if (!result.success) {
            // Re-map each issue so that the error path starts with the original index.
            result.error.issues.forEach((issue) => {
              ctx.addIssue({
                ...issue,
                path: [index, ...issue.path],
              });
            });
          }
        });
      })
      // Remove the empty objects *after* validation so that the indices used in
      // any validation errors above still match the original array positions.
      .transform((arr) => arr.filter((item) => isNonEmptyObject(item)))
      // Re-parse the filtered array with the original item schema to ensure the
      // correct output type. This parse should succeed because we already
      // validated each item in the `superRefine` above.
      .pipe(z.array(itemSchema))
  );
};

/**
 * Preprocessor that handles decimal number input, allowing intermediate states
 * like "123." while typing, but converting to proper numbers for validation.
 * Returns null for empty values to signal deletion to the backend.
 */
const decimalNumberPreprocessor = (val: string | number | null | undefined) => {
  if (val === null || val === undefined) {
    return null;
  }

  if (typeof val === 'number') {
    return val;
  }

  const stringVal = String(val).trim();

  if (stringVal === '') {
    return null;
  }

  // Allow intermediate states ending with decimal point for better UX
  let cleanedValue = stringVal;
  if (stringVal.endsWith('.')) {
    cleanedValue = stringVal.slice(0, -1);
  }

  if (cleanedValue === '') {
    return null;
  }

  const parsed = parseFloat(cleanedValue);

  if (isNaN(parsed)) {
    throw new Error('Must be a valid number');
  }

  if (parsed < 0) {
    throw new Error('Must be a non-negative number');
  }

  return parsed;
};

/**
 * A Zod schema for optional decimal numbers that handles string input
 * and allows intermediate typing states like "123." while providing
 * proper validation and transformation to numbers. Returns null for empty
 * values to signal deletion to the backend.
 *
 * UPDO: Right now, this is transforming values to null to support deleting data,
 * but it is type-casting the values to undefined in order to avoid conflicts with
 * the API. This is a temporary solution, we should have a better way to handle this
 * (e.g. by supporting null values in the API).
 */
export const zDecimalNumber = z.preprocess(decimalNumberPreprocessor, z.number().optional().nullable()) as z.ZodType<
  number | undefined
>;
